import { defineStore } from 'pinia'
import { useSystemStore } from '@sto/system.store'
import { useAuthStore } from '@sto/auth.store'
import * as SaleService from '@ser/sale'
import * as AssetService from '@ser/asset'

import { shallowRef } from 'vue'

import SalesCreateStepSetup from '@par/sales/SalesCreateStepSetup.vue'
import SalesCreateStepTimes from '@par/sales/SalesCreateStepTimes.vue'
import SalesCreateStepLocation from '@par/sales/SalesCreateStepLocation.vue'
import SalesCreateStepCatalogue from '@par/sales/SalesCreateStepCatalogue.vue'
import SalesCreateStepPostSale from '@par/sales/SalesCreateStepPostSale.vue'
import SalesCreateStepActivate from '@par/sales/SalesCreateStepActivate.vue'
import {
    exportSaleJson,
    markManyAsUnsold,
    syncSaleSectionsToSaleType,
    updateSaleCode,
    withdrawLotFromSale,
    withdrawManyLots
} from "@ser/sale";
import {markLotAsUnsold} from "@ser/sale/api.js";
import * as VehicleService from "@ser/vehicle/index.js";

export const useSaleStore = defineStore('sale', {

    state: () => {
        return {
            _SYSTEM_STORE: useSystemStore(),
            _SALE_SERVICE: SaleService,
            _sale: null,
            _sale_loaded: false,
            _sale_draft_loading: false,
            _duplicate_sale_template: null,
            sale_error_heading: '',
            sale_error_message: '',
            sale_loaded: false,
            sale_step_id: 'setup',
            create_steps_base: [
                {
                    id: 'setup',
                    name: 'Setup',
                    description: 'Choose which type of sale to add',
                    complete: false,
                    status: 'current',
                    //status: 'upcoming',
                    component: shallowRef(SalesCreateStepSetup)
                },
                {
                    id: 'times',
                    name: 'Sale Times',
                    description: 'When is the sale running',
                    complete: false,
                    status: 'upcoming',
                    component: shallowRef(SalesCreateStepTimes)
                },
                {
                    id: 'format',
                    name: 'Location',
                    description: 'Sale and viewing',
                    complete: false,
                    status: 'upcoming',
                    component: shallowRef(SalesCreateStepLocation)
                },
                {
                    id: 'catalogue',
                    name: 'Catalogue',
                    description: 'Publish the catalogue and invite entries',
                    complete: false,
                    status: 'upcoming',
                    component: shallowRef(SalesCreateStepCatalogue)
                },
                {
                    id: 'post_sale',
                    name: 'Post Sale',
                    description: 'Post-sale settings',
                    complete: false,
                    status: 'upcoming',
                    component: shallowRef(SalesCreateStepPostSale)
                },
                {
                    id: 'activate',
                    name: 'Confirm',
                    description: 'Create the sale',
                    complete: false,
                    status: 'upcoming',
                    component: shallowRef(SalesCreateStepActivate)
                },
                
            ],
            create_steps_complete_base: false,

            // assign lot modal
            _assign_lot_modal_active: false,
            _assign_lot_asset_id: null,
            _assign_lot_auctioneer_id: null,
            // change lot modal
            _change_lot_modal_active: false,
            _change_lot_asset_id: null,
            _change_lot_auctioneer_id: null,
            _change_lot_current_number: null,
            // add to sale modal
            _assign_to_sale_modal_active: false,
            _assign_to_sale_asset_id: null,
            _assign_to_sale_lot_number: null
        }
    },

    getters: {
        sale: state => state._sale,
        sale_loaded: state => state._sale_loaded,
        create_steps: state => state.create_steps_base,
        create_steps_complete: state => state.create_steps_complete_base,
        current_active_step: state => {
            let current_step_index = state.create_steps_base.findIndex(s => s.status == 'current')
            current_step_index = current_step_index ?? state.create_steps_base - 1
            return state.create_steps_base[current_step_index].id

        },

        duplicate_sale_template: state => state._duplicate_sale_template,

        assign_lot_modal_active: state => state._assign_lot_modal_active,
        assign_lot_asset_id: state => state._assign_lot_asset_id,
        assign_lot_auctioneer_id: state => state._assign_lot_auctioneer_id,

        change_lot_modal_active: state => state._change_lot_modal_active,
        change_lot_asset_id: state => state._change_lot_asset_id,
        change_lot_auctioneer_id: state => state._change_lot_auctioneer_id,
        change_lot_current_number: state => state._change_lot_current_number,

        assign_to_sale_modal_active: state => state._assign_to_sale_modal_active,
        assign_to_sale_asset_id: state => state._assign_to_sale_asset_id,
        assign_to_sale_lot_number: state => state._assign_to_sale_lot_number,
    },

    actions: {
        /**
         * get a sale by id
         * @param {int} sale_id 
         * @returns 
         */
        async getById(sale_id) {
            return SaleService.getById(sale_id)
        },

        /**
         * get the sales for a given user
         * @param {int} user_id 
         * @param {array} params 
         */
        async getForUser(user_id, params) {
            return SaleService.getForUser(user_id, params)
        },

        async getForAuthUser(params) {
            const AUTH_STORE = useAuthStore()
            return SaleService.getForUser(AUTH_STORE.user?.id, params)
        },

        /**
         * create a new sale type
         * @param {*} data 
         * @returns 
         */
        async createSaleType(data) {
            return SaleService.createSaleType(data)
        },

        /**
         * update a sale type
         * @param {int} sale_type_id
         * @param {*} data
         * @returns
         */
        async updateSaleType(sale_type_id, data) {
            return SaleService.updateSaleType(sale_type_id, data)
        },

        /**
         * add a label to an existing sale type
         * this has the side effect of removing other existing labels on this sale type
         */
        async addLabelToSaleType(sale_type_id = null, data = null) {
            return SaleService.addLabelToSaleType(sale_type_id, data)
        },

        /**
         * get the sale types for an auctioneer
         */
        async getAuctioneerSaleTypes(auctioneer_id = null) {
            return SaleService.getAuctioneerSaleTypes(auctioneer_id)
        },

        /**
         * get the sale sections for a given auctioneer
         */
        async getAuctioneerSaleSections(auctioneer_id = null) {
            return SaleService.getAuctioneerSaleSections(auctioneer_id)
        },

        /**
         * get the sale codes for a given auctioneer
         */
        async getAuctioneerSaleCodes(auctioneer_id = null) {
            return SaleService.getAuctioneerSaleCodes(auctioneer_id)
        },

        /**
         * create a new sale section
         * @param {*} data 
         * @returns 
         */
        async createSaleSection(data) {
            return SaleService.createSaleSection(data)
        },

        /**
         * create a new sale code
         * @param {*} data 
         * @returns 
         */
        async createSaleCode(data) {
            return SaleService.createSaleCode(data)
        },

        /**
         * attach types to a sale section
         * @param {int} sale_section_id 
         * @param {obj} data - including sale_type_ids property with stringified array of integers e.g. { sale_type_ids: "1,2,3" }
         */
        async attachSaleTypesToSaleSection(sale_section_id, data) {
            return SaleService.attachSaleTypesToSaleSection(sale_section_id, data)
        },

        /**
         * sync types to a sale section
         * @param {int} sale_section_id
         * @param {obj} data - including sale_type_ids property with stringified array of integers e.g. { sale_type_ids: "1,2,3" }
         */
        async syncSaleTypesToSaleSection(sale_section_id, data) {
            return SaleService.syncSaleTypesToSaleSection(sale_section_id, data)
        },

        /**
         * attach codes to a sale section
         * @param {int} sale_section_id
         * @param {obj} data - including sale_type_ids property with stringified array of integers e.g. { sale_type_ids: "1,2,3" }
         */
        async attachSaleCodesToSaleSection(sale_section_id, data) {
            return SaleService.attachSaleCodesToSaleSection(sale_section_id, data)
        },

        /**
         * sync codes to a sale section
         * @param {int} sale_section_id
         * @param {obj} data - including sale_type_ids property with stringified array of integers e.g. { sale_type_ids: "1,2,3" }
         */
        async syncSaleCodesToSaleSection(sale_section_id, data) {
            return SaleService.syncSaleCodesToSaleSection(sale_section_id, data)
        },

        /**
         * attach sections to a sale type
         * @param {int} sale_section_id 
         * @param {obj} data - including sale_type_ids property with stringified array of integers e.g. { sale_type_ids: "1,2,3" }
         */
        async attachSaleSectionsToSaleType(sale_type_id, data) {
            return SaleService.attachSaleSectionsToSaleType(sale_type_id, data)
        },

        /**
         * sync sale sections to a sale type
         * @param sale_type_id
         * @param data
         * @returns {Promise<obj>}
         */
        async syncSaleSectionsToSaleType(sale_type_id, data) {
            return SaleService.syncSaleSectionsToSaleType(sale_type_id, data)
        },

        /**
         * attach sale sections to a sale code
         * @param {*} sale_code_id 
         * @param {*} data 
         * @returns 
         */
        async attachSaleSectionsToSaleCode(sale_code_id, data) {
            return SaleService.attachSaleSectionsToSaleCode(sale_code_id, data)
        },

        /**
         * attach sale sections to a sale code
         * @param {*} sale_code_id
         * @param {*} data
         * @returns
         */
        async syncSaleSectionsToSaleCode(sale_code_id, data) {
            return SaleService.syncSaleSectionsToSaleCode(sale_code_id, data)
        },

        /**
         * attach a feature field to a sale id
         * @param {*} sale_code_id 
         * @param {*} data 
         * @returns 
         */
        async attachFeatureFieldToSaleCode(sale_code_id, data) {
            return SaleService.attachFeatureFieldToSaleCode(sale_code_id, data)
        },

        /**
         * create a feature field
         * @param {obj} data 
         */
        async createField(data) {
            return SaleService.createField(data)
        },

        /**
         * get the sale types assocuaed with a given user
         * @param {*} user_id 
         * @param {*} params 
         * @returns 
         */
        async getUserSaleTypes(user_id, params = []) {
            return SaleService.getUserSaleTypes(user_id, params)
        },

        /**
         * get the sale sections assocuaed with a given user
         * @param {*} user_id 
         * @param {*} params 
         * @returns 
         */
        async getUserSaleSections(user_id, params = []) {
            return SaleService.getUserSaleSections(user_id, params)
        },

        /**
         * get the sale codes assocuaed with a given user
         * @param {*} user_id 
         * @param {*} params 
         * @returns 
         */
        async getUserSaleCodes(user_id, params = []) {
            return SaleService.getUserSaleCodes(user_id, params)
        },

        /**
         * get a single sale type by id
         * @param {*} sale_type_id 
         * @returns 
         */
        async getSaleTypeById(sale_type_id) {
            return SaleService.getSaleTypeById(sale_type_id)
        },

        /**
         * get a single sale section by id
         * @param {*} sale_section_id 
         * @returns 
         */
        async getSaleSectionById(sale_section_id) {
            return SaleService.getSaleSectionById(sale_section_id)
        },

        /**
         * get a single sale code by id
         * @param {*} sale_code_id 
         * @returns 
         */
        async getSaleCodeById(sale_code_id) {
            return SaleService.getSaleCodeById(sale_code_id)
        },

        /**
         * update a sale code
         */
        async updateSaleCode(sale_code_id, data) {
            return SaleService.updateSaleCode(sale_code_id, data)
        },

        /**
         * update a sale section
         */
        async updateSaleSection(sale_section_id, data) {
            return SaleService.updateSaleSection(sale_section_id, data)
        },

        /**
         * create a draft sale
         * @param {*} data 
         */
        async createDraftSale(data) {
            return SaleService.createDraftSale(data)
        },

        /**
         * set the times data for a draft sale 
         * @param {*} sale_id 
         * @param {*} data 
         * @returns 
         */
        async setDraftSaleTimes(sale_id, data) {
            return SaleService.setDraftSaleTimes(sale_id, data)
        },

        /**
         * get the sale codes for a given organisation
         * @param {int} organisation_id 
         * @returns 
         */
        async getOrganisationSaleCodes(organisation_id) {
            return SaleService.getOrganisationSaleCodes(organisation_id)
        },

        /**
         * get the eligible assets for a given sale
         * @param {arr} params 
         * @returns 
         */
        async getEligibleAssets(params) {
            return SaleService.getEligibleAssets(params)
        },

        /**
         * get the eligible sales for a given asset
         * @param {arr} params 
         * @returns 
         */
        async getEligibleSales(params) {
            return SaleService.getEligibleSales(params)
        },

        /**
         * activate a  sale
         * @param {int} sale_id 
         * @returns 
         */
        async activate(sale_id) {
            return SaleService.activate(sale_id)
        },

        /**
         * delete sales belonging to this user
         * @param {obj} data 
         * @returns 
         */
        async batchDelete(data) {
            return SaleService.batchDelete(data)
        },

        /**
         * update sales belonging to this user
         * @param {obj} data 
         * @returns 
         */
        async batchUpdate(data) {
            return SaleService.batchUpdate(data)
        },

        /**
         * get the sale related feature fields ("custom fields") for the specified user
         * @param {int} user_id 
         * @param {array} params 
         * @returns 
         */
        async getUserFeatureFields(user_id, params) {
            return SaleService.getUserFeatureFields(user_id, params)
        },

        /**
         * get a feature by id
         * @param {int} feature_field_id 
         * @returns 
         */
        async getFeatureField(feature_field_id) {
            return SaleService.getFeatureField(feature_field_id)
        },

        /**
         * update a feature field
         * @param {int} feature_field_id 
         * @param {obj} data 
         * @returns 
         */
        async updateFeatureField(feature_field_id, data) {
            return SaleService.updateFeatureField(feature_field_id, data)
        },

        /**
         * add a lot an auctioneer's sale
         * @param {int} auctioneer_id 
         * @param {int} sale_id 
         * @param {obj} data 
         * @returns 
         */
        async addLotToAuctioneerSale(auctioneer_id, sale_id, data) {
            return SaleService.addLotToAuctioneerSale(auctioneer_id, sale_id, data)
        },

        async getLots(params = []) {
            return SaleService.getLots(params)
        },

        /**
         * remove a lot number from an asset
         */
        async removeLotNumber(asset_id, lot_number, auctioneer_id) {
            return AssetService.removeLotNumber(asset_id, { lot_number: lot_number, auctioneer_id: auctioneer_id })
        },

        /**
         * delete an auctioneer's sale code
         * @param auctioneer_id
         * @param sale_code_id
         * @returns {Promise<obj>}
         */
        async deleteAuctioneerSaleCode(auctioneer_id, sale_code_id) {
            return SaleService.deleteAuctioneerSaleCode(auctioneer_id, sale_code_id)
        },

        /**
         * delete an auctioneer's sale section
         * @param auctioneer_id
         * @param sale_section_id
         * @returns {Promise<obj>}
         */
        async deleteAuctioneerSaleSection(auctioneer_id, sale_section_id) {
            return SaleService.deleteAuctioneerSaleSection(auctioneer_id, sale_section_id)
        },

        /**
         * delete an auctioneer's sale type
         * @param auctioneer_id
         * @param sale_type_id
         * @returns {Promise<obj>}
         */
        async deleteAuctioneerSaleType(auctioneer_id, sale_type_id) {
            return SaleService.deleteAuctioneerSaleType(auctioneer_id, sale_type_id)
        },

        /**
         * get the in progress draft sale
         * @param {*} sale_id 
         * @returns 
         */
        async getDraftSale(sale_id, set_state) {

            // check to see if we're loading already
            if (this._sale_draft_loading) return
            // set the flag
            this._sale_draft_loading = true
            // if this is a different sale, do a reset
            if (this._sale?.id && (this._sale?.id != sale_id)) this.resetStore()

            const draft_sale = await SaleService.getDraftSale(sale_id)
            if (set_state) this.setStepsState(draft_sale)
            this._sale = draft_sale
            this._sale_loaded = true
            this.resetSaleDraftLoadingFlag(false, 100)
        },

        async exportBidJS(sale_id) {

            const export_data = await SaleService.exportBidJS(sale_id)

            let csv = "data:text/csv;charset=utf-8," + encodeURI(export_data.csv_stream);
            let link = document.createElement("a");
            link.setAttribute("href", csv);
            link.setAttribute("download", "sale-id-"+sale_id+".csv");
            document.body.appendChild(link); // Required for FF
            link.click();
        },

        async exportWebSaleReport(sale_id) {
            const export_data = await SaleService.exportWebSaleReport(sale_id)

            let csv = "data:text/csv;charset=utf-8," + encodeURI(export_data.csv_stream);
            let link = document.createElement("a");
            link.setAttribute("href", csv);
            link.setAttribute("download", "website-sale-report-"+sale_id+".csv");
            document.body.appendChild(link); // Required for FF
            link.click();
        },

        async exportSaleJson(sale_id) {

            const export_data = await SaleService.exportSaleJson(sale_id)

            // Here you have your data in JSON format, you can process it or
            // convert it into a Blob and download as a .json file.
            const blob = new Blob([JSON.stringify(export_data.data)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);

            // Create an invisible anchor element
            const a = document.createElement('a');
            a.style.display = 'none';
            document.body.appendChild(a);

            // Set the href and download attribute for the anchor element
            a.href = url;
            a.download = 'sale-'+sale_id+'.json';

            // Trigger click on the anchor element
            // which will prompt the browser to download the JSON file
            a.click();

            // Cleanup
            URL.revokeObjectURL(url);
            document.body.removeChild(a);

        },

        async exportInfomart(sale_id) {

            const export_data = await SaleService.exportInfomart(sale_id)
            if (!export_data?.response?.csv_stream) throw "Invalid export data"
            const csv_stream = export_data.response.csv_stream.replace(/\"\^/g, '^').replace(/\^\"/g, '^');

            let csv = "data:text/csv;charset=utf-8," + encodeURI(csv_stream);
            let link = document.createElement("a");
            link.setAttribute("href", csv);
            link.setAttribute("download", export_data.response.filename+'.txt');
            document.body.appendChild(link); // Required for FF
            link.click();
        },

        async exportUserSaleSettings(user_id, params) {

            const export_data = await SaleService.exportUserSaleSettings(user_id, params)

            let csv = "data:text/csv;charset=utf-8,"
                + export_data.map(e => e.join(",")).join("\n");

            let encoded_csv = encodeURI(csv);
            let link = document.createElement("a");
            link.setAttribute("href", encoded_csv);
            link.setAttribute("download", "sale-settings.csv");
            document.body.appendChild(link); // Required for FF
            link.click();
        },

        /**
         * publish a sale
         */
        async publish(sale_id) {
            return SaleService.publish(sale_id)
        },

        /**
         * unpublish a sale
         */
        async unpublish(sale_id) {
            return SaleService.unpublish(sale_id)
        },

        /**
         * reset the sale draft loading flag
         * optoinally pass in a time delay - sueful because some watch events happen collectively
         */
        resetSaleDraftLoadingFlag(flag_value = false, delay = 0) {
            setTimeout(() => {
                this._sale_draft_loading = flag_value
            }, delay)
        },

        /**s
         * update a sale 
         * @param {*} sale_id 
         * @param {*} data 
         * @returns 
         */
        async updateSale(sale_id, data) {
            return SaleService.updateSale(sale_id, data)
        },

        /**
         * activate a specific step
         * 
         * @param {str} step_id for the target step
         */
        activateStep(step_id) {
            const current_step_index = this.create_steps_base.findIndex(s => s.status == 'current')
            this.create_steps_base[current_step_index].status = this.create_steps_base[current_step_index].complete ? 'complete' : 'upcoming'
            const target_step_index = this.create_steps_base.findIndex(s => s.id == step_id)
            if (!isNaN(target_step_index) && target_step_index >= 0) this.create_steps_base[target_step_index].status = 'current'
        },

        /**
         * activate tne next step in the arrray
         */
        activateNextStep() {
            const current_step_index = this.create_steps_base.findIndex(s => s.status == 'current')
            // complete this step
            this.create_steps_base[current_step_index].complete = true
            this.create_steps_base[current_step_index].status = 'complete'
            // not the final step
            if (current_step_index < this.create_steps_base.length) {
                //this.active_step_id = this.create_steps_base[current_step_index + 1].id
                this.create_steps_base[current_step_index + 1].status = 'current'
            } else {
                // TODO handle final step sceanrio
            }
        },

        /**
         * activate the previous step
         */
        activatePrevStep() {
            const current_step_index = this.create_steps_base.findIndex(s => s.status == 'current')
            // not the first step
            if (current_step_index > 0) {
                this.create_steps_base[current_step_index].status = this.create_steps_base[current_step_index].complete ? 'complete' : 'upcoming'
                this.create_steps_base[current_step_index - 1].status = 'current'
            }
        },

        /**
         * set the steps state
         * @param {sale} sale 
         */
        setStepsState(sale) {

            let sale_step_index
            
            if (sale) {
                sale_step_index = this.create_steps_base.findIndex(s => s.id == 'setup')
                this.create_steps_base[sale_step_index].status = 'complete'
                this.create_steps_base[sale_step_index].complete = true
            }

            if (sale?.starts_at_date) {
                sale_step_index = this.create_steps_base.findIndex(s => s.id == 'times')
                this.create_steps_base[sale_step_index].status = 'complete'
                this.create_steps_base[sale_step_index].complete = true
            }

            if (sale?.format || sale?.description) {
                sale_step_index = this.create_steps_base.findIndex(s => s.id == 'format')
                this.create_steps_base[sale_step_index].status = 'complete'
                this.create_steps_base[sale_step_index].complete = true
            }

            if (sale?.catalogue_status) {
                sale_step_index = this.create_steps_base.findIndex(s => s.id == 'catalogue')
                this.create_steps_base[sale_step_index].status = 'complete'
                this.create_steps_base[sale_step_index].complete = true
            }

            const first_incomplete_step = this.create_steps_base.findIndex(s => s.complete == false)
            // activate the first avaiable step OR the final step if all completes
            if (!isNaN(first_incomplete_step) && this.create_steps_base[first_incomplete_step]) {
                this.create_steps_base[first_incomplete_step].status = 'current'
            } else {
                this.create_steps_base[(this.create_steps_base.length - 1)].status = 'current'
            }
        },

        setSale(sale) {
            this._sale = sale
        },

        clearSale() {
            this._sale = null
        },

        setDuplicateSaleTemplate(sale_template) {
            this._duplicate_sale_template = sale_template
        },

        /**
         * reset the sale store
         */
        resetStore() {
            this._sale = null
            this._duplicate_sale_template = null
            this.sale_error_heading = ''
            this.sale_error_message = ''
            this._sale_loaded = false
            this.active_step_id = 'setup'
            this.create_steps_base.forEach((step, index) => {
                step.status = index === 0 ? 'current' : 'upcoming'
                step.complete = false
            })
            this.create_steps_complete_base = false
        },

        /**
         * activate the assign lot number modal
         */
        assignLotNumber( modal_active = false, asset_id = null, auctioneer_id = null) {
            this._assign_lot_modal_active = modal_active
            this._assign_lot_asset_id = asset_id
            this._assign_lot_auctioneer_id = auctioneer_id
        },
        closeAssignLotNumberModal() {
            this._assign_lot_modal_active = false
            this._assign_lot_asset_id = null
            this._assign_lot_auctioneer_id = null
        },

        changeLotNumber( modal_active = false, asset_id = null, auctioneer_id = null, lot_number = null) {
            this._change_lot_modal_active = modal_active
            this._change_lot_asset_id = asset_id
            this._change_lot_auctioneer_id = auctioneer_id
            this._change_lot_current_number = lot_number
        },
        closeChangeLotNumberModal() {
            this._change_lot_modal_active = false
            this._change_lot_asset_id = null
            this._change_lot_auctioneer_id = null
            this._change_lot_current_number = null
        },

        /**
         * actiuvate the assign to sale modal
         */
        assignToSale(modal_active = false, asset_id = null, lot_number = null) {
            this._assign_to_sale_modal_active = modal_active
            this._assign_to_sale_asset_id = asset_id
            this._assign_to_sale_lot_number = lot_number
        },
        closeAssignToSaleModal() {
            this._assign_to_sale_modal_active = false
            this._assign_to_sale_asset_id = null
            this._assign_to_sale_lot_number = null
        },

        async withdrawLotFromSale(auctioneer_id, sale_id, lot_id) {
            return SaleService.withdrawLotFromSale(auctioneer_id, sale_id, lot_id)
        },

        async markLotAsUnsold(auctioneer_id, sale_id, lot_id) {
            return SaleService.markLotAsUnsold(auctioneer_id, sale_id, lot_id)
        },

        async withdrawManyLots(sale_id, data) {
            return SaleService.withdrawManyLots(sale_id, data)
        },

        async markManyAsUnsold(sale_id, data) {
            return SaleService.markManyAsUnsold(sale_id, data)
        }
    }
})